import { orderBy, uniqueId } from 'lodash-es'
import { makeAutoObservable, observable } from 'mobx'
import React, { type CSSProperties } from 'react'

export type ModalOption = {
  type?: 'default' | 'full'
  hasCloseButton?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClose?: (...args: any[]) => boolean
  onAfterClose?: () => void
  dataAtribute?: Record<string, string>
  modalName: string
  /** 모달의 접근성 이름. 제목이 content 안에 있어 헤더로 쓸 수 없는 모달이 지정한다. */
  ariaLabel?: string
  headerOption?: {
    position?: 'center' | 'left' | 'right'
    hasBottomBorder?: boolean
    style?: CSSProperties
  }
  modalStyle?: {
    padding?: string
    width?: string
    /** 판 모서리 — 스케일(`--Radius-*`)로 안 맞는 값을 쓰는 판만 지정한다(미지정 시 `--Radius-300`). */
    borderRadius?: string
  }
  orderingNumber?: number
  isCloseable?: boolean
}

export type ModalContent = { header?: React.ReactNode; content: React.ReactNode }

class ModalState {
  private _id = uniqueId()
  modalContent: ModalContent
  opt: ModalOption & { orderingNumber: number }

  constructor(modalContent: ModalContent, opt: ModalOption) {
    this.modalContent = modalContent
    this.opt = {
      ...opt,
      orderingNumber: opt.orderingNumber ?? 50,
      isCloseable: opt.isCloseable ?? true,
    }
  }

  get id() {
    return this._id
  }
}

export class ModalService {
  private _modalList: ModalState[] = []
  isShowLoader = false

  get modalList() {
    return orderBy(this._modalList, 'opt.orderingNumber', 'desc')
  }

  constructor() {
    makeAutoObservable<this, '_modalList'>(this, {
      _modalList: observable,
    })
  }

  openModal = (modalContent: ModalContent | React.ReactNode, opt: ModalOption) => {
    window.freshpaint?.track('[공통] 모달 OPEN', { name: opt.modalName })

    if (React.isValidElement(modalContent)) {
      this._modalList.push(new ModalState({ content: modalContent }, opt))
    } else {
      this._modalList.push(new ModalState(modalContent as ModalContent, opt))
    }
  }

  closeModal = () => {
    if (this.modalList.length > 0) {
      const lastOrderedModalState = this.modalList[this.modalList.length - 1]
      this._modalList = this._modalList.filter((modalState) => {
        return modalState.id !== lastOrderedModalState?.id
      })

      if (lastOrderedModalState) {
        lastOrderedModalState.opt.onAfterClose?.()
      }
    }
  }

  closeAllModal = () => {
    this._modalList = []
  }

  showLoader = () => {
    this.isShowLoader = true
  }

  hideLoader = () => {
    this.isShowLoader = false
  }
}

const modalService = new ModalService()
export default modalService
